import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Checkbox } from './ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Loader2, FileText, FileType2, GraduationCap, Download, Save, Sparkles, PenTool,
  Search, Check, ArrowLeft, Eye, Pencil, FilePlus2, FileImage, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const ContentWriterDialog = ({ open, onOpenChange, application, onSaved }) => {
  const { api } = useAuth();
  const [step, setStep] = useState('choose');
  // choose | program-picker | doc-picker | generating | preview
  const [selectedType, setSelectedType] = useState(null); // sop | resume
  const [extraInstructions, setExtraInstructions] = useState('');
  const [content, setContent] = useState('');
  const [documentsUsed, setDocumentsUsed] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filename, setFilename] = useState('');

  // Program picker state
  const [detectedProgram, setDetectedProgram] = useState('');
  const [programQuery, setProgramQuery] = useState('');
  const [programResults, setProgramResults] = useState([]);
  const [loadingPrograms, setLoadingPrograms] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState(null);
  // { id, name, display } — what we actually pass to backend

  // Document picker state
  const [availableDocs, setAvailableDocs] = useState([]);
  const [selectedDocIds, setSelectedDocIds] = useState(() => new Set());
  const [loadingDocs, setLoadingDocs] = useState(false);
  // The program payload finalised before opening the doc picker
  const [pendingProgram, setPendingProgram] = useState(null);

  const pollTimer = useRef(null);

  // Detect target program from application on open (resolve ID → name via backend)
  useEffect(() => {
    if (!open || !application) return;
    let cancelled = false;
    (async () => {
      // First set quick fallback from whatever is already on the object
      const admission = application?.desk_data?.admission || {};
      let quickName = admission.program_name || '';
      // Only use admission.program if it doesn't look like a UUID
      const looksLikeId = (s) => typeof s === 'string' && /^[0-9a-f-]{32,}$/i.test(s);
      if (!quickName && admission.program && !looksLikeId(admission.program)) {
        quickName = admission.program;
      }
      setDetectedProgram(quickName);
      setSelectedProgram(null);
      setProgramQuery('');
      setProgramResults([]);
      // Now resolve from backend for ID → name mapping
      try {
        const res = await api().get(`/applications/${application.id}/content-writer/context`);
        if (cancelled) return;
        const ctx = res.data || {};
        if (ctx.program_name) {
          setDetectedProgram(ctx.program_name);
          // Pre-select so generation passes the full program metadata
          setSelectedProgram({
            id: ctx.program_id || null,
            name: ctx.program_name,
            degree_type_name: ctx.degree_type_name || '',
            university_name: ctx.university_name || '',
          });
        }
      } catch (_e) { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [open, application, api]);

  // Search programs as the user types
  useEffect(() => {
    if (step !== 'program-picker') return;
    const q = programQuery.trim();
    setLoadingPrograms(true);
    const handle = setTimeout(async () => {
      try {
        const res = await api().get('/content-writer/programs-search', {
          params: { q: q || '', limit: 40 },
        });
        setProgramResults(Array.isArray(res.data) ? res.data : []);
      } catch (_e) {
        setProgramResults([]);
      } finally {
        setLoadingPrograms(false);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [programQuery, step, api]);

  useEffect(() => () => {
    if (pollTimer.current) clearTimeout(pollTimer.current);
  }, []);

  const reset = () => {
    setStep('choose');
    setSelectedType(null);
    setExtraInstructions('');
    setContent('');
    setDocumentsUsed([]);
    setFilename('');
    setSelectedProgram(null);
    setProgramQuery('');
    setProgramResults([]);
    setAvailableDocs([]);
    setSelectedDocIds(new Set());
    setPendingProgram(null);
    if (pollTimer.current) { clearTimeout(pollTimer.current); pollTimer.current = null; }
  };

  const close = () => {
    reset();
    onOpenChange(false);
  };

  const loadAvailableDocs = async () => {
    setLoadingDocs(true);
    try {
      const res = await api().get(`/applications/${application.id}/content-writer/documents`);
      const docs = Array.isArray(res.data?.documents) ? res.data.documents : [];
      setAvailableDocs(docs);
      // Pre-select all readable + priority documents by default
      const preset = new Set(
        docs.filter(d => d.readable && d.is_priority).map(d => d.id)
      );
      // If nothing was priority, select all readable docs by default
      if (preset.size === 0) {
        docs.forEach(d => { if (d.readable) preset.add(d.id); });
      }
      setSelectedDocIds(preset);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load documents');
      setAvailableDocs([]);
      setSelectedDocIds(new Set());
    } finally {
      setLoadingDocs(false);
    }
  };

  const openDocPicker = async (type, programPayload) => {
    setSelectedType(type);
    setPendingProgram(programPayload || null);
    setStep('doc-picker');
    await loadAvailableDocs();
  };

  const toggleDocSelection = (docId) => {
    setSelectedDocIds(prev => {
      const next = new Set(prev);
      if (next.has(docId)) next.delete(docId); else next.add(docId);
      return next;
    });
  };

  const selectAllReadable = () => {
    const all = new Set(availableDocs.filter(d => d.readable).map(d => d.id));
    setSelectedDocIds(all);
  };

  const clearAllDocs = () => setSelectedDocIds(new Set());

  const pollJob = async (jobId) => {
    try {
      const res = await api().get(`/content-writer/job/${jobId}`);
      const job = res.data || {};
      if (job.status === 'complete') {
        setContent(job.content || '');
        setDocumentsUsed(job.documents_used || []);
        const nameSlug = (application?.student_name || 'applicant').replace(/\s+/g, '_');
        // Default filename without extension; extension is appended at export time
        setFilename(`${selectedType.toUpperCase()}_${nameSlug}`);
        setGenerating(false);
        setStep('preview');
        toast.success(`${selectedType === 'sop' ? 'SOP' : 'Resume'} ready — review and save/download`);
        return;
      }
      if (job.status === 'failed') {
        setGenerating(false);
        setStep('doc-picker');
        toast.error(job.error || 'Generation failed. Please try again.');
        return;
      }
      // still processing → poll again in 3s (max ~3 min total)
      pollTimer.current = setTimeout(() => pollJob(jobId), 3000);
    } catch (err) {
      setGenerating(false);
      setStep('doc-picker');
      toast.error(err.response?.data?.detail || 'Failed to fetch job status');
    }
  };

  const startGeneration = async () => {
    const type = selectedType;
    if (!type) return;
    if (selectedDocIds.size === 0) {
      toast.error('Please select at least one document for the AI to read.');
      return;
    }
    setGenerating(true);
    setStep('generating');
    const body = {
      type,
      extra_instructions: extraInstructions || null,
      selected_document_ids: Array.from(selectedDocIds),
    };
    if (type === 'sop') {
      const prog = pendingProgram || selectedProgram;
      if (prog?.id) body.target_program_id = prog.id;
      if (prog?.name) body.target_program_name = prog.name;
      else if (!prog && detectedProgram) body.target_program_name = detectedProgram;
    }
    try {
      const res = await api().post(`/applications/${application.id}/content-writer/generate`, body);
      const jobId = res.data?.job_id;
      if (!jobId) throw new Error('No job_id returned');
      pollJob(jobId);
    } catch (err) {
      setGenerating(false);
      setStep('doc-picker');
      toast.error(err.response?.data?.detail || err.message || 'Failed to start generation');
    }
  };

  const handleTypeClick = (type) => {
    if (type === 'resume') {
      // Resume flow: skip program picker, go straight to doc picker
      openDocPicker('resume', null);
      return;
    }
    // SOP: confirm/pick program first → then doc picker
    setSelectedType('sop');
    setStep('program-picker');
  };

  const _triggerBlobDownload = (blob, suggestedName, mimeType) => {
    const blobObj = blob instanceof Blob ? blob : new Blob([blob], { type: mimeType });
    const url = URL.createObjectURL(blobObj);
    const link = document.createElement('a');
    link.href = url;
    link.download = suggestedName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 1500);
  };

  const downloadPdf = async () => {
    if (!content.trim()) return;
    setSaving(true);
    try {
      const baseName = (filename || 'document').replace(/\.(pdf|docx?)$/i, '');
      const res = await api().post(`/applications/${application.id}/content-writer/save-pdf`, {
        type: selectedType, content, filename: `${baseName}.pdf`,
      });
      const docId = res.data?.document?.id;
      const docFilename = res.data?.document?.filename || `${baseName}.pdf`;
      if (!docId) throw new Error('No document id returned');
      const fileRes = await api().get(`/documents/${docId}/download`, { responseType: 'blob' });
      _triggerBlobDownload(fileRes.data, docFilename, 'application/pdf');
      toast.success('PDF downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Failed to download PDF');
    } finally {
      setSaving(false);
    }
  };

  const downloadDocx = async () => {
    if (!content.trim()) return;
    setSaving(true);
    try {
      const baseName = (filename || 'document').replace(/\.(pdf|docx?)$/i, '');
      const res = await api().post(`/applications/${application.id}/content-writer/save-docx`, {
        type: selectedType, content, filename: `${baseName}.docx`,
      });
      const docId = res.data?.document?.id;
      const docFilename = res.data?.document?.filename || `${baseName}.docx`;
      if (!docId) throw new Error('No document id returned');
      const fileRes = await api().get(`/documents/${docId}/download`, { responseType: 'blob' });
      _triggerBlobDownload(
        fileRes.data, docFilename,
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      );
      toast.success('Word document downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Failed to download Word document');
    } finally {
      setSaving(false);
    }
  };

  const saveToApplication = async () => {
    if (!content.trim()) return;
    setSaving(true);
    try {
      const baseName = (filename || 'document').replace(/\.(pdf|docx?)$/i, '');
      const res = await api().post(`/applications/${application.id}/content-writer/save-pdf`, {
        type: selectedType, content, filename: `${baseName}.pdf`,
      });
      toast.success(`Saved as ${res.data.document.filename} to this application's documents`);
      if (typeof onSaved === 'function') onSaved(res.data.document);
      close();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save to application');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); else onOpenChange(v); }}>
      <DialogContent className="sm:max-w-[720px] max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <PenTool className="w-5 h-5 text-amber-500" />
            AI Content Writer
          </DialogTitle>
          <DialogDescription>
            {application?.student_name
              ? `Generate professional documents for ${application.student_name} using AI + uploaded files`
              : 'Generate professional documents using AI'}
          </DialogDescription>
        </DialogHeader>

        {step === 'choose' && (
          <div className="space-y-4 overflow-y-auto flex-1 pr-1">
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => handleTypeClick('sop')}
                disabled={generating}
                data-testid="content-type-sop-btn"
                className="group relative p-5 rounded-xl border-2 border-border hover:border-amber-400 hover:bg-amber-50 dark:hover:bg-slate-900/30 transition text-left"
              >
                <div className="flex items-center gap-2 mb-2">
                  <GraduationCap className="w-6 h-6 text-amber-500" />
                  <span className="font-semibold text-base">Statement of Purpose</span>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  A 750–900 word SOP tailored to the target programme & university, deeply relevant to the applicant's background.
                </p>
                <Sparkles className="absolute top-3 right-3 w-4 h-4 text-amber-400 opacity-0 group-hover:opacity-100 transition" />
              </button>

              <button
                onClick={() => handleTypeClick('resume')}
                disabled={generating}
                data-testid="content-type-resume-btn"
                className="group relative p-5 rounded-xl border-2 border-border hover:border-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/30 transition text-left"
              >
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="w-6 h-6 text-emerald-600" />
                  <span className="font-semibold text-base">Resume / CV</span>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  A clean, ATS-friendly one-page résumé built from attached documents (transcripts, test scores, experience).
                </p>
                <Sparkles className="absolute top-3 right-3 w-4 h-4 text-emerald-400 opacity-0 group-hover:opacity-100 transition" />
              </button>
            </div>

            <div className="pt-2 border-t">
              <Label htmlFor="extra" className="text-xs">Additional instructions for the AI (optional)</Label>
              <Textarea
                id="extra"
                placeholder="e.g., emphasise my research experience, mention my plan to return to Pakistan, keep tone formal..."
                value={extraInstructions}
                onChange={(e) => setExtraInstructions(e.target.value)}
                rows={3}
                className="resize-none text-sm mt-1"
                data-testid="content-extra-instructions-input"
              />
            </div>

            <div className="p-3 rounded-md bg-amber-50 dark:bg-slate-900/30 border border-amber-200 text-[11px] text-slate-800 dark:text-amber-200">
              <strong>Heads up:</strong> Next you'll choose which documents the AI should read (resume, transcripts, etc.). Generation takes 30–90 seconds and costs <strong>1 credit</strong>. You can edit and export the result as Word or PDF.
            </div>
          </div>
        )}

        {step === 'program-picker' && (
          <div className="space-y-3 overflow-y-auto flex-1 pr-1">
            {detectedProgram ? (
              <div className="p-3 rounded-md bg-green-50 dark:bg-green-950/30 border border-green-300">
                <div className="flex items-center gap-2 mb-1">
                  <Check className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-medium text-green-900 dark:text-green-200">
                    Programme detected from admissions tab
                  </span>
                </div>
                <p className="text-sm font-semibold">{detectedProgram}</p>
                {(selectedProgram?.degree_type_name || selectedProgram?.university_name) && (
                  <p className="text-[11px] text-green-800 dark:text-green-300 mt-0.5">
                    {[selectedProgram?.degree_type_name, selectedProgram?.university_name].filter(Boolean).join(' · ')}
                  </p>
                )}
                <div className="flex gap-2 mt-3">
                  <Button
                    size="sm"
                    className="bg-amber-500 hover:bg-slate-800"
                    onClick={() => openDocPicker('sop', selectedProgram || { name: detectedProgram })}
                    data-testid="content-use-detected-btn"
                  >
                    <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                    Use this programme
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => { setDetectedProgram(''); setSelectedProgram(null); }} data-testid="content-change-program-btn">
                    Change programme
                  </Button>
                </div>
              </div>
            ) : (
              <div className="p-3 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-300 text-xs">
                <strong>No target programme found on this application.</strong> Please search and select the subject the student is applying to — the SOP will be written around it.
              </div>
            )}

            {!detectedProgram && (
              <>
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-muted-foreground" />
                  <Input
                    autoFocus
                    placeholder="Search programmes (e.g. data science, MBA, law...)"
                    value={programQuery}
                    onChange={(e) => setProgramQuery(e.target.value)}
                    className="pl-8"
                    data-testid="content-program-search-input"
                  />
                </div>

                <div className="border rounded-md max-h-[320px] overflow-y-auto">
                  {loadingPrograms && (
                    <div className="p-4 flex items-center justify-center text-xs text-muted-foreground">
                      <Loader2 className="w-4 h-4 animate-spin mr-2" /> Searching…
                    </div>
                  )}
                  {!loadingPrograms && programResults.length === 0 && (
                    <div className="p-4 text-center text-xs text-muted-foreground">
                      {programQuery.trim() ? 'No matching programmes found.' : 'Start typing to search your partner universities.'}
                    </div>
                  )}
                  {!loadingPrograms && programResults.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => {
                        setSelectedProgram(p);
                        openDocPicker('sop', p);
                      }}
                      className="w-full text-left px-3 py-2 hover:bg-amber-50 dark:hover:bg-slate-900/30 border-b last:border-0 transition"
                      data-testid={`content-program-option-${p.id}`}
                    >
                      <div className="text-sm font-medium">{p.name}</div>
                      <div className="text-[11px] text-muted-foreground">
                        {p.degree_type_name ? `${p.degree_type_name} · ` : ''}{p.university_name || 'Unknown university'}
                      </div>
                    </button>
                  ))}
                </div>

                <div className="text-[10px] text-muted-foreground">
                  Can't find the programme? Type it exactly as you want it to appear in the SOP:
                </div>
                <div className="flex gap-2">
                  <Input
                    placeholder="Type custom programme name..."
                    value={programQuery}
                    onChange={(e) => setProgramQuery(e.target.value)}
                    data-testid="content-program-custom-input"
                  />
                  <Button
                    size="sm"
                    disabled={!programQuery.trim()}
                    onClick={() => openDocPicker('sop', { name: programQuery.trim() })}
                    className="bg-amber-500 hover:bg-slate-800 shrink-0"
                    data-testid="content-use-custom-btn"
                  >
                    Use as programme
                  </Button>
                </div>
              </>
            )}
          </div>
        )}

        {step === 'doc-picker' && (
          <div className="space-y-3 overflow-hidden flex-1 flex flex-col pr-1">
            <div className="p-3 rounded-md bg-amber-50 dark:bg-slate-900/30 border border-amber-200 text-[12px] text-slate-800 dark:text-amber-200">
              <div className="font-semibold mb-1">Select documents the AI should read</div>
              <p className="text-[11px] leading-relaxed">
                Pick the files (resume, transcripts, certificates, passport, test scores…) you want the AI to base the {selectedType === 'sop' ? 'SOP' : 'Resume'} on. Supported: PDF, JPG/PNG, DOCX. Files marked unreadable will be skipped.
              </p>
            </div>

            <div className="flex items-center justify-between gap-2 text-xs">
              <div className="text-muted-foreground">
                <span data-testid="content-docs-selected-count" className="font-semibold text-slate-800 dark:text-amber-300">
                  {selectedDocIds.size}
                </span> of {availableDocs.filter(d => d.readable).length} readable selected
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={selectAllReadable} disabled={loadingDocs} data-testid="content-docs-select-all">Select all</Button>
                <Button size="sm" variant="ghost" onClick={clearAllDocs} disabled={loadingDocs} data-testid="content-docs-clear">Clear</Button>
              </div>
            </div>

            <div className="border rounded-md flex-1 overflow-y-auto">
              {loadingDocs && (
                <div className="p-6 flex items-center justify-center text-xs text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading documents…
                </div>
              )}
              {!loadingDocs && availableDocs.length === 0 && (
                <div className="p-6 text-center text-xs text-muted-foreground space-y-2">
                  <AlertCircle className="w-6 h-6 mx-auto text-amber-500" />
                  <div>No documents found on this application.</div>
                  <div>Please upload the student's transcripts / resume / passport in the Documents tab first.</div>
                </div>
              )}
              {!loadingDocs && availableDocs.map((d) => {
                const checked = selectedDocIds.has(d.id);
                const ext = (d.ext || '').toLowerCase();
                const isImg = ['jpg','jpeg','png','webp','gif'].includes(ext);
                const Icon = isImg ? FileImage : (ext === 'docx' ? FilePlus2 : FileText);
                return (
                  <label
                    key={d.id}
                    htmlFor={`cw-doc-${d.id}`}
                    className={`flex items-start gap-3 px-3 py-2.5 border-b last:border-0 transition cursor-pointer ${
                      d.readable
                        ? (checked ? 'bg-amber-50 dark:bg-slate-900/30' : 'hover:bg-zinc-50 dark:hover:bg-zinc-900/40')
                        : 'opacity-50 cursor-not-allowed'
                    }`}
                    data-testid={`content-doc-row-${d.id}`}
                  >
                    <Checkbox
                      id={`cw-doc-${d.id}`}
                      checked={checked}
                      disabled={!d.readable}
                      onCheckedChange={() => d.readable && toggleDocSelection(d.id)}
                      className="mt-0.5"
                      data-testid={`content-doc-checkbox-${d.id}`}
                    />
                    <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${d.is_priority ? 'text-amber-500' : 'text-muted-foreground'}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{d.filename}</div>
                      <div className="text-[10.5px] text-muted-foreground flex items-center gap-2 flex-wrap">
                        <span>{d.document_type_name}</span>
                        {ext && <span className="uppercase font-mono px-1 rounded bg-zinc-200 dark:bg-zinc-700 text-[9px]">{ext}</span>}
                        {d.is_priority && <span className="text-amber-500 font-medium">priority</span>}
                        {!d.readable && <span className="text-amber-600 font-medium">unreadable — convert to PDF/DOCX/image</span>}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>

            <div className="text-[10px] text-muted-foreground">
              Tip: include at least the resume and the latest transcript so the AI has actual academic / employment data to draw from.
            </div>
          </div>
        )}

        {step === 'generating' && (
          <div className="flex flex-col items-center justify-center py-12 space-y-4">
            <div className="relative">
              <Loader2 className="w-12 h-12 animate-spin text-amber-500" />
              <Sparkles className="absolute -top-1 -right-1 w-5 h-5 text-amber-400 animate-pulse" />
            </div>
            <div className="text-center space-y-1">
              <p className="font-medium">Reading documents and writing…</p>
              <p className="text-xs text-muted-foreground">This usually takes 20–90 seconds. Keep this window open.</p>
            </div>
          </div>
        )}

        {step === 'preview' && (
          <div className="space-y-3 overflow-hidden flex-1 flex flex-col pr-1">
            {documentsUsed.length > 0 && (
              <div className="text-[11px] text-muted-foreground">
                <span className="font-medium">Based on:</span> {documentsUsed.join(', ')}
              </div>
            )}
            <div>
              <Label htmlFor="filename" className="text-xs">Filename (extension auto-set on export)</Label>
              <Input
                id="filename"
                value={filename}
                onChange={(e) => setFilename(e.target.value)}
                className="h-9"
                data-testid="content-filename-input"
              />
            </div>
            <Tabs defaultValue="edit" className="flex-1 flex flex-col overflow-hidden">
              <TabsList className="self-start">
                <TabsTrigger value="edit" data-testid="content-tab-edit">
                  <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
                </TabsTrigger>
                <TabsTrigger value="preview" data-testid="content-tab-preview">
                  <Eye className="w-3.5 h-3.5 mr-1.5" /> Preview
                </TabsTrigger>
              </TabsList>
              <TabsContent value="edit" className="flex-1 flex flex-col overflow-hidden mt-2">
                <Textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  className="flex-1 font-mono text-xs leading-relaxed min-h-[320px] resize-none"
                  data-testid="content-preview-textarea"
                />
                <p className="text-[10px] text-muted-foreground mt-1">
                  {content.length.toLocaleString()} characters · Markdown formatting (# heading, **bold**, - bullets)
                </p>
              </TabsContent>
              <TabsContent value="preview" className="flex-1 overflow-hidden mt-2">
                <div
                  className="h-full overflow-y-auto rounded-md border bg-white dark:bg-zinc-900 px-5 py-4 prose prose-sm dark:prose-invert max-w-none prose-headings:font-bold prose-h1:text-2xl prose-h1:text-slate-800 prose-h1:border-b prose-h1:pb-2 prose-h2:text-lg prose-h2:text-slate-800 prose-h2:mt-4 prose-h3:text-base prose-h3:text-teal-700 prose-p:leading-relaxed prose-li:my-0.5"
                  data-testid="content-rendered-preview"
                >
                  <ReactMarkdown>{content || '_No content yet._'}</ReactMarkdown>
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">
                  Live preview · switch to <strong>Edit</strong> tab to modify
                </p>
              </TabsContent>
            </Tabs>
          </div>
        )}

        <DialogFooter className="shrink-0 pt-2 border-t">
          {step === 'preview' ? (
            <div className="flex w-full items-center justify-between gap-2">
              <Button variant="ghost" onClick={() => setStep('choose')} disabled={saving} data-testid="content-back-btn">
                <ArrowLeft className="w-4 h-4 mr-1" /> Regenerate
              </Button>
              <div className="flex gap-2 flex-wrap justify-end">
                <Button variant="outline" onClick={downloadDocx} disabled={saving || !content.trim()} data-testid="content-download-docx-btn">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileType2 className="w-4 h-4 mr-2" />}
                  Download Word
                </Button>
                <Button variant="outline" onClick={downloadPdf} disabled={saving || !content.trim()} data-testid="content-download-btn">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                  Download PDF
                </Button>
                <Button onClick={saveToApplication} disabled={saving || !content.trim()} data-testid="content-save-btn" className="bg-amber-500 hover:bg-slate-800">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Attach to Application
                </Button>
              </div>
            </div>
          ) : step === 'program-picker' ? (
            <div className="flex w-full items-center justify-between gap-2">
              <Button variant="ghost" onClick={() => setStep('choose')} data-testid="content-picker-back-btn">
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              <Button variant="outline" onClick={close}>Cancel</Button>
            </div>
          ) : step === 'doc-picker' ? (
            <div className="flex w-full items-center justify-between gap-2">
              <Button variant="ghost" onClick={() => setStep(selectedType === 'sop' ? 'program-picker' : 'choose')} disabled={generating} data-testid="content-doc-back-btn">
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={close} disabled={generating}>Cancel</Button>
                <Button
                  onClick={startGeneration}
                  disabled={generating || loadingDocs || selectedDocIds.size === 0}
                  className="bg-amber-500 hover:bg-slate-800"
                  data-testid="content-doc-generate-btn"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generate {selectedType === 'sop' ? 'SOP' : 'Resume'} ({selectedDocIds.size})
                </Button>
              </div>
            </div>
          ) : (
            <Button variant="outline" onClick={close} disabled={generating}>Close</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ContentWriterDialog;
